import {
  type CreateWalletTransaction,
  CreateWalletTransactionSchema,
  type Wallet,
} from '@monorepo/shared'
import { format } from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import { Calendar } from '@/shared/components/ui/calendar'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { cn } from '@/shared/lib/utils'
import { useAddCredits } from '../../hooks/useWallet'

interface AddCreditsDialogProps {
  wallet: Wallet
  customerName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AddCreditsDialog({
  wallet,
  customerName,
  open,
  onOpenChange,
}: AddCreditsDialogProps) {
  // Form states
  const [creditAmount, setCreditAmount] = useState('')
  const [creditDescription, setCreditDescription] = useState('')
  const [referenceId, setReferenceId] = useState('')
  const [referenceType, setReferenceType] = useState<
    'invoice' | 'payment' | 'adjustment'
  >('adjustment')
  const [creditType, setCreditType] = useState<'free' | 'purchased'>(
    'purchased'
  )
  const [expiryDate, setExpiryDate] = useState<string | undefined>(undefined)
  const [expiryDateOpen, setExpiryDateOpen] = useState(false)

  const addCreditsMutation = useAddCredits()

  const handleSubmit = async () => {
    if (!wallet || !creditAmount) return

    try {
      const transactionData: CreateWalletTransaction = {
        transaction_type: 'credit',
        amount: parseFloat(creditAmount),
        credit_type: creditType || undefined,
        description: creditDescription || 'Manual credit addition',
        reference_id: referenceId || undefined,
        reference_type: referenceType || undefined,
        expiry_date: expiryDate || undefined,
        metadata: {},
      }

      // Client-side validation
      CreateWalletTransactionSchema.parse(transactionData)

      await addCreditsMutation.mutateAsync({
        walletId: wallet.id,
        transactionData,
      })

      // Reset form and close
      resetForm()
      onOpenChange(false)
    } catch (error) {
      toast.error(extractErrorMessage(error))
    }
  }

  const resetForm = () => {
    setCreditAmount('')
    setCreditDescription('')
    setReferenceId('')
    setReferenceType('adjustment')
    setCreditType('purchased')
    setExpiryDate(undefined)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Add Credits</DialogTitle>
          <DialogDescription>
            Add credits to {customerName}'s wallet
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4 py-4'>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='credit-amount'>Amount</Label>
              <div className='flex items-center gap-2'>
                <Input
                  id='credit-amount'
                  type='number'
                  step='0.01'
                  value={creditAmount}
                  onChange={e => setCreditAmount(e.target.value)}
                  placeholder='0.00'
                  className='flex-1'
                />
                <span className='text-muted-foreground'>{wallet.currency}</span>
              </div>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='credit-description'>Description</Label>
              <Input
                id='credit-description'
                value={creditDescription}
                onChange={e => setCreditDescription(e.target.value)}
                placeholder='Credit description (optional)'
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='reference-type'>Reference Type</Label>
              <Select
                value={referenceType}
                onValueChange={(value: 'invoice' | 'payment' | 'adjustment') =>
                  setReferenceType(value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='adjustment'>Adjustment</SelectItem>
                  <SelectItem value='payment'>Payment</SelectItem>
                  <SelectItem value='invoice'>Invoice</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='reference-id'>Reference ID (Optional)</Label>
              <Input
                id='reference-id'
                value={referenceId}
                onChange={e => setReferenceId(e.target.value)}
                placeholder='Reference ID'
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='credit-type'>Credit Type</Label>
              <Select
                value={creditType}
                onValueChange={(value: 'free' | 'purchased') =>
                  setCreditType(value)
                }
              >
                <SelectTrigger id='credit-type'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='purchased'>Purchased</SelectItem>
                  <SelectItem value='free'>Free (Promotional)</SelectItem>
                </SelectContent>
              </Select>
              <div className='text-xs text-muted-foreground'>
                Type of credit being added
              </div>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='expiry-date'>Expiry Date (Optional)</Label>
              <Popover open={expiryDateOpen} onOpenChange={setExpiryDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    id='expiry-date'
                    variant='outline'
                    className={cn(
                      'w-full justify-between font-normal',
                      !expiryDate && 'text-muted-foreground'
                    )}
                  >
                    {expiryDate
                      ? format(new Date(expiryDate), 'PPP')
                      : 'No expiry date'}
                    <CalendarIcon className='h-4 w-4' />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className='w-auto overflow-hidden p-0'
                  align='start'
                >
                  <Calendar
                    mode='single'
                    selected={expiryDate ? new Date(expiryDate) : undefined}
                    captionLayout='dropdown'
                    onSelect={date => {
                      if (date) {
                        setExpiryDate(date.toISOString())
                        setExpiryDateOpen(false)
                      }
                    }}
                    disabled={date => date < new Date()}
                    className='rounded-md border'
                  />
                </PopoverContent>
              </Popover>
              <div className='text-xs text-muted-foreground'>
                When credits will expire (leave empty for no expiry)
              </div>
              {expiryDate && (
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  onClick={() => setExpiryDate(undefined)}
                  className='h-6 text-xs'
                >
                  Clear expiry date
                </Button>
              )}
            </div>
          </div>

          <div className='flex justify-end gap-2 pt-4'>
            <Button variant='outline' onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!creditAmount || addCreditsMutation.isPending}
            >
              {addCreditsMutation.isPending ? 'Adding...' : 'Add Credits'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
